import { FormattedTextString } from "client/Misc/commonTypes";
import { resolveFormattedTextString } from "client/Misc/utils";
import {
	AriaAttributes,
	ReactElement,
	useContext,
	useMemo
} from "react";
import { ActionListItem } from "../ActionList/ActionListItem";
import { Button, ButtonKind } from "../Button/Button";
import { ItemResponsiveBehavior } from "../CollapsibleList/CollapsibleList";
import { ToolbarContext, ToolbarPriority } from "./Toolbar";
import { ToolbarItem } from "./ToolbarItem";
import {
	FontIconName,
	IconValue,
	SvgIconValue
} from "../Icon/Icon";
import { useCallbackWithoutArguments } from "client/Hooks";

export type ToolbarButtonProps = {
	text: string | FormattedTextString;
	onClick?(): void;
	trackingId?: string;
	responsiveBehavior?: ItemResponsiveBehavior;
	isDisabled?: boolean;
	bubbleClick?: boolean;
	tooltip?: string;
	"aria-current"?: AriaAttributes["aria-current"];
}
& (
	{
		kind: Exclude<ButtonKind, "transparent-for-list">;
		icon?: FontIconName | SvgIconValue;
		rightIcon?: FontIconName | SvgIconValue;
	}
	|
	{
		kind: "transparent-for-list";
		icon?: IconValue;
		rightIcon?: IconValue;
	}
);

export const ToolbarButton = (props: ToolbarButtonProps): ReactElement => {
	const {
		kind,
		text,
		icon,
		responsiveBehavior,
		trackingId,
		isDisabled,
		onClick,
		bubbleClick,
		rightIcon,
		tooltip
	} = props;

	const priority = useMemo(
		(): number => {
			if (responsiveBehavior === "hide") {
				return ToolbarPriority.LowPriorityItem;
			}
			if (kind === "primary") {
				return ToolbarPriority.PrimaryButton;
			}

			return ToolbarPriority.Default;
		},
		[responsiveBehavior, kind]
	);

	const clickHandler: (() => void) | undefined = useCallbackWithoutArguments(onClick);

	const { buttonSize } = useContext(ToolbarContext);

	return (
		<ToolbarItem
			onExecute={clickHandler}
			priority={priority}
			responsiveBehavior={responsiveBehavior}
			isDisabled={isDisabled}
			renderNormalMode={(
				ref,
				onKeyDown,
				tabIndex
			) => {
				return (
					<Button
						{...(
							// This code construction is here to make TS happy
							kind === "transparent-for-list"
								? {
									kind,
									icon,
									rightIcon
								}
								: {
									kind,
									icon,
									rightIcon
								}
						)}
						ref={ref}
						onKeyDown={onKeyDown}
						tabIndex={tabIndex}
						text={text}
						onClick={clickHandler}
						trackingId={trackingId}
						isDisabled={isDisabled}
						bubbleClick={bubbleClick}
						size={buttonSize}
						tooltip={tooltip}
						aria-current={props["aria-current"]}
					/>
				);
			}}
			renderCollapsedMode={() => {
				return (
					<ActionListItem
						onClick={clickHandler}
						text={resolveFormattedTextString(text)}
						icon={icon}
						trackingId={trackingId}
						isDisabled={isDisabled}
						bubbleClick={bubbleClick}
						rightIcon={rightIcon}
						title={tooltip}
					/>
				);
			}}
		/>
	);
};
ToolbarButton.displayName = "Toolbar.Button";
